// =============================================================================
// GOUVERNANCE - Espace de pilotage CTO
// =============================================================================
// Accès : Icône ⚙ dans le header (Super Admin / Admin uniquement)
// Structure 3 menus + Dashboard racine :
//   • Les Gardiens   → Gamora / Groot / Gatekeeper / Nebula
//   • Configuration  → Config globale / Sécurité / Intégrations / Listes
//                      & Statuts / Documents / Workflows
//   • Opérations     → Tickets / Règles / Coûts / Audit / Base de Connaissance
// =============================================================================

import { useState, useEffect, useContext } from "react";
import {
  Ticket,
  Shield,
  GitBranch,
  Scale,
  Lock,
  Plug,
  Settings,
  FileText,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Activity,
  AlertTriangle,
  Users,
  Database,
  Server,
  List,
  DollarSign,
  Mail,
  Phone
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { useAuth } from "../../contexts/AuthContext";
import PermissionContext from "../../contexts/PermissionContext";

// Import des widgets
import GouvTickets from "./widgets/GouvTickets";
import GouvGroot from "./widgets/GouvGroot";
import GouvGatekeeper from "./widgets/GouvGatekeeper";
import GouvSecurity from "./widgets/GouvSecurity";
// GouvIntegrations remplacé par MultiConnectionsManager (plus complet)
import MultiConnectionsManager from "../management/MultiConnectionsManager";
import GouvAudit from "./widgets/GouvAudit";
import GouvRules from "./widgets/GouvRules";
import GouvDashboard from "./widgets/GouvDashboard";
import GouvConfig from "./widgets/GouvConfig";
import GouvDocRequirements from "./widgets/GouvDocRequirements";
import GouvListesStatuts from "./widgets/GouvListesStatuts";
import GouvCosts from "./widgets/GouvCosts";
import GouvWorkflows from "./widgets/GouvWorkflows";
import GouvKnowledgeBase from "./widgets/GouvKnowledgeBase";
import GouvGamora from "./widgets/GouvGamora";
import GouvNebula from "./widgets/GouvNebula";
import GouvRocket from "./widgets/GouvRocket";
import GouvEmailConfig from "./widgets/GouvEmailConfig";
import GouvAudrey from "./widgets/GouvAudrey";
import GouvFindings from "./widgets/GouvFindings";
import GouvOutilsAvances from "./widgets/GouvOutilsAvances";
import GouvControleConformite from "./widgets/GouvControleConformite";

const API = process.env.REACT_APP_BACKEND_URL;

// Définition des 9 widgets (Dashboard ajouté en premier)
const GOVERNANCE_WIDGETS = [
  {
    id: "dashboard",
    label: "Dashboard",
    description: "Vue d'ensemble",
    icon: Activity,
    component: GouvDashboard
  },
  {
    id: "gamora",
    label: "Qualité des données",
    description: "Gamora",
    icon: Shield,
    component: GouvGamora
  },
  {
    id: "costs",
    label: "Coûts",
    description: "Budget & APIs",
    icon: DollarSign,
    component: GouvCosts
  },
  {
    id: "tickets",
    label: "Tickets",
    description: "Dev & Business",
    icon: Ticket,
    component: GouvTickets
  },
  {
    id: "groot",
    label: "Contrôle du code",
    description: "Groot",
    icon: Shield,
    component: GouvGroot
  },
  {
    id: "gatekeeper",
    label: "Contrôle avant mise en ligne",
    description: "Gatekeeper",
    icon: GitBranch,
    component: GouvGatekeeper
  },
  {
    id: "nebula",
    label: "Qualité en fonctionnement",
    description: "Nebula",
    icon: Activity,
    component: GouvNebula
  },
  {
    id: "rocket",
    label: "Tests de sécurité",
    description: "Rocket",
    icon: Shield,
    component: GouvRocket
  },
  {
    id: "findings",
    label: "Points à vérifier",
    description: "Findings",
    icon: AlertTriangle,
    component: GouvFindings
  },
  {
    id: "rules",
    label: "Règles de contrôle",
    description: "Policy",
    icon: Scale,
    component: GouvRules
  },
  {
    id: "documents",
    label: "Documents",
    description: "Pièces exigées à chaque étape",
    icon: FileText,
    component: GouvDocRequirements
  },
  {
    id: "statuts",
    label: "Listes & Statuts",
    description: "Choix proposés dans les formulaires",
    icon: List,
    component: GouvListesStatuts
  },
  {
    id: "workflows",
    label: "Workflows",
    description: "Cycles de vie versionnés",
    icon: GitBranch,
    component: GouvWorkflows
  },
  {
    id: "security",
    label: "Utilisateurs",
    description: "Comptes, rôles et droits",
    icon: Lock,
    component: GouvSecurity
  },
  {
    id: "integrations",
    label: "Intégrations",
    description: "APIs & Connexions",
    icon: Plug,
    component: MultiConnectionsManager
  },
  {
    id: "config",
    label: "Configuration",
    description: "Globale",
    icon: Settings,
    component: GouvConfig
  },
  {
    id: "emails",
    label: "Emails",
    description: "Modèles et destinataires",
    icon: Mail,
    component: GouvEmailConfig
  },
  {
    id: "audrey",
    label: "Audrey",
    description: "Adresse publique de la voix",
    icon: Phone,
    component: GouvAudrey
  },
  {
    id: "knowledge-base",
    label: "Base de Connaissance",
    description: "FAQ & Aide",
    icon: FileText,
    component: GouvKnowledgeBase
  },
  {
    id: "audit",
    label: "Audit",
    description: "Logs & Traces",
    icon: FileText,
    component: GouvAudit
  },
  {
    id: "outils-avances",
    label: "Supervision du Bureau",
    description: "File historique · Trace de l'agent · Activité de la communication · Pipeline facturation",
    icon: Settings,
    color: "text-zinc-300",
    bgColor: "bg-zinc-500/10",
    borderColor: "border-zinc-500/30",
    component: GouvOutilsAvances
  },
  {
    id: "controle-conformite",
    label: "Contrôle de conformité (ancien)",
    description: "Ancien écran « Joey »",
    icon: Shield,
    color: "text-zinc-300",
    bgColor: "bg-zinc-500/10",
    borderColor: "border-zinc-500/30",
    component: GouvControleConformite
  }
];

// T-431 / PN-22 + T-431b — deux parties : « Paramètres » (ADMIN du tenant) et « Outils avancés » (super admin).
// Le Dashboard reste en racine, réservé au super admin (il montre les gardiens techniques).
// Entrées d'« Paramètres » qui suivent les drapeaux du tenant (capabilities `gouvernance.*`) : l'admin les voit si le drapeau est
// vrai (absent ou faux = fermé) ; le super admin les garde dans « Outils avancés ». Tickets : jamais pour un non-admin.
// T-431c : Audit et Configuration sont des écrans de PLATEFORME (documents internes, tous tenants) → super admin, quel que soit le drapeau.
const WIDGETS_A_DRAPEAU = {
  integrations: "gouvernance.integrations",
  tickets: "gouvernance.tickets",
};
const WIDGETS_PARAMETRES_DE_BASE = ["documents", "statuts", "emails", "audrey", "security"];
const GOVERNANCE_GROUPS = [
  {
    id: "root",
    label: null,
    superAdminOnly: true,
    widgetIds: ["dashboard"]
  },
  {
    id: "parametres",
    label: "Paramètres",
    phrase: "Ce que l'administrateur règle pour son espace : pièces exigées, listes, emails, voix, comptes et, si votre espace les ouvre, intégrations et tickets.",
    icon: Settings,
    adminOnly: true,
    widgetIds: [...WIDGETS_PARAMETRES_DE_BASE, ...Object.keys(WIDGETS_A_DRAPEAU)]
  },
  {
    id: "outils-avances",
    label: "Outils avancés",
    phrase: "Contrôles techniques de la plateforme, réservés au super administrateur.",
    icon: Shield,
    superAdminOnly: true,
    widgetIds: ["gamora", "groot", "gatekeeper", "nebula", "rocket", "findings", "rules",
                "config", "integrations", "workflows", "tickets", "costs", "audit", "knowledge-base",
                "outils-avances", "controle-conformite"]
  }
];

/**
 * Entrées visibles, par rôle (source unique : Governance et la cloche d'OmniHub).
 * super admin → tout ; admin → « Paramètres » de base + entrées dont le drapeau tenant est vrai ; autre rôle → rien.
 */
export function groupesAccessibles({ estSuperAdmin, estAdmin, drapeauActif }) {
  if (estSuperAdmin) {
    return GOVERNANCE_GROUPS.map((g) => (g.id === "parametres" ? { ...g, widgetIds: WIDGETS_PARAMETRES_DE_BASE } : g));
  }
  if (!estAdmin) return [];
  return GOVERNANCE_GROUPS.filter((g) => g.adminOnly).map((g) => ({
    ...g,
    widgetIds: g.widgetIds.filter((id) => !WIDGETS_A_DRAPEAU[id] || drapeauActif(WIDGETS_A_DRAPEAU[id])),
  }));
}

/** La cloche « Mes tickets » n'ouvre la Gouvernance que si l'entrée « Tickets » existe pour ce rôle. */
export function peutOuvrirTickets({ estSuperAdmin, estAdmin, drapeauActif }) {
  return groupesAccessibles({ estSuperAdmin, estAdmin, drapeauActif }).some((g) => g.widgetIds.includes("tickets"));
}

const WIDGETS_BY_ID = Object.fromEntries(GOVERNANCE_WIDGETS.map(w => [w.id, w]));

export default function Governance({ onClose, currentUser, currentTenant, tenants = [], initialWidget }) {
  const { user } = useAuth();
  const effectiveUser = currentUser || user;
  const estSuperAdmin = Boolean(
    effectiveUser?.is_super_admin || effectiveUser?.role === "super_admin" || effectiveUser?.rôle === "super_admin"
  );
  const estAdmin = effectiveUser?.role === "admin";
  const perm = useContext(PermissionContext);
  // Fail-closed : sans contexte de permissions, aucun drapeau n'est ouvert.
  const drapeauActif = (id) => Boolean(perm?.isFeatureEnabled?.(id));
  const groupesVisibles = groupesAccessibles({ estSuperAdmin, estAdmin, drapeauActif });
  const idsAutorises = new Set(groupesVisibles.flatMap((g) => g.widgetIds));
  const widgetParDefaut = estSuperAdmin ? "dashboard" : (idsAutorises.has("documents") ? "documents" : null);
  const [activeWidget, setActiveWidget] = useState(
    initialWidget && idsAutorises.has(initialWidget) ? initialWidget : widgetParDefaut
  );
  // Sections collapse/expand persistées (gardiens / config / ops). Le groupe "root"
  // (Dashboard) reste toujours visible — pas de label, pas collapsable.
  const COLLAPSE_KEY = "gouv:collapsedGroups";
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });
  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const [stats, setStats] = useState({
    tickets: { total: 0, in_progress: 0, blocked: 0 }
  });

  // Charger les stats globales
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Stats tickets
      const ticketsRes = await fetch(`${API}/api/oh/tickets/stats/overview`);
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setStats(prev => ({
          ...prev,
          tickets: {
            total: data.total || 0,
            in_progress: data.active || 0,
            blocked: data.blocked || 0
          }
        }));
      }
    } catch (err) {
      console.error("Erreur chargement stats gouvernance:", err);
    }
  };

  const activeWidgetConfig = idsAutorises.has(activeWidget) ? GOVERNANCE_WIDGETS.find(w => w.id === activeWidget) : null;
  const ActiveComponent = activeWidgetConfig?.component;

  // Sidebar rendering helper (Restructuration 2026-05-12)
  // Groupe le rendu par section (Gardiens / Configuration / Opérations).
  // Mobile: liste scrollable verticale plate (pas d'onglets horizontaux).
  // Desktop: sidebar verticale fixe, sections séparées par leur header.
  const renderNavItem = (widget) => {
    if (!widget) return null;
    const isActive = activeWidget === widget.id;
    return (
      <button
        key={widget.id}
        onClick={() => setActiveWidget(widget.id)}
        data-testid={`gouv-nav-${widget.id}`}
        aria-current={isActive ? "page" : undefined}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left"
        style={{
          background: isActive ? "var(--oh-accent-bg)" : "transparent",
          border: `0.5px solid ${isActive ? "var(--oh-accent)" : "transparent"}`,
        }}
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: "var(--oh-bg-elevated)" }}>
          <widget.icon className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm truncate"
                  style={{ fontWeight: 500, color: isActive ? "var(--oh-accent-text)" : "var(--oh-text)" }}>
              {widget.label}
            </span>
            {widget.id === "tickets" && stats.tickets.blocked > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                {stats.tickets.blocked}
              </Badge>
            )}
          </div>
          <p className="text-xs truncate" style={{ color: "var(--oh-text-muted)" }}>{widget.description}</p>
        </div>
        {isActive && <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "var(--oh-text-muted)" }} />}
      </button>
    );
  };

  const renderGroup = (group) => {
    const widgets = group.widgetIds.map(id => WIDGETS_BY_ID[id]).filter(Boolean);
    if (widgets.length === 0) return null;
    const collapsible = !!group.label;
    const isCollapsed = collapsible && collapsedGroups.has(group.id);
    return (
      <div key={group.id} className="space-y-1" data-testid={`gouv-group-${group.id}`}>
        {group.label && (
          <button
            type="button"
            onClick={() => toggleGroup(group.id)}
            aria-expanded={!isCollapsed}
            data-testid={`gouv-group-toggle-${group.id}`}
            className="w-full px-3 pt-3 pb-1 flex items-center gap-2 transition-colors"
            style={{ color: "var(--oh-text-secondary)" }}
          >
            {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {group.icon && <group.icon className="w-3.5 h-3.5" />}
            <span className="text-sm" style={{ fontWeight: 500 }}>{group.label}</span>
            <span className="ml-auto text-xs" style={{ color: "var(--oh-text-muted)" }}>{widgets.length}</span>
          </button>
        )}
        {!isCollapsed && group.phrase && (
          <p className="px-3 pb-1 text-xs" style={{ color: "var(--oh-text-muted)", lineHeight: 1.6 }}
             data-testid={`gouv-phrase-${group.id}`}>
            {group.phrase}
          </p>
        )}
        {!isCollapsed && widgets.map(renderNavItem)}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--oh-bg)" }}>
      {/* Header Gouvernance */}
      <div className="px-4 md:px-6 py-3 md:py-4" style={{ background: "var(--oh-bg-surface)", borderBottom: "0.5px solid var(--oh-border)" }}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors shrink-0"
              style={{ border: "1px solid var(--oh-border)" }}
              aria-label="Revenir à l'accueil"
              data-testid="gouv-close"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: "var(--oh-text-secondary)" }} />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl flex items-center gap-2" style={{ fontWeight: 500, color: "var(--oh-text)" }}>
                <Settings className="w-5 h-5 shrink-0" style={{ color: "var(--oh-text-secondary)" }} />
                <span className="truncate">Gouvernance</span>
              </h1>
              <p className="text-xs md:text-sm truncate" style={{ color: "var(--oh-text-muted)" }}>{estSuperAdmin ? "Paramètres de l'espace et outils avancés" : "Paramètres de l'espace"}</p>
            </div>
          </div>

          {/* Indicateurs rapides — masqués sur mobile */}
          <div className="hidden md:flex items-center gap-4">
            {/* T-431 / PN-21 : « Deploy OK » et « violations » retirés — le scanner Groot n'examine plus
                qu'un fichier (DETTE-110) : un feu vert ou un compteur sur cette base serait inventé. */}
            {/* Tickets en cours */}
            {estSuperAdmin && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "var(--oh-stat-blue-bg)", border: "0.5px solid var(--oh-stat-blue-border)" }}>
              <Ticket className="w-4 h-4" style={{ color: "var(--oh-stat-blue-text)" }} />
              <span className="text-sm" style={{ color: "var(--oh-stat-blue-text)" }}>
                {stats.tickets.in_progress} en cours
              </span>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Layout principal : Sidebar + Contenu (stack en mobile, side-by-side en md+) */}
      <div className="flex flex-col md:flex-row">
        {/* Sidebar groupée — liste scrollable verticale (mobile ET desktop) */}
        <aside style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }} className="md:w-72 border-b md:border-b-0 md:border-r md:min-h-[calc(100vh-73px)] md:max-h-[calc(100vh-73px)] md:overflow-y-auto">
          <nav className="p-2 md:p-3 space-y-2 max-h-[40vh] md:max-h-none overflow-y-auto">
            {groupesVisibles.map(renderGroup)}
          </nav>
        </aside>

        {/* Contenu du widget actif */}
        <main className="flex-1 p-3 md:p-6 overflow-auto min-w-0">
          {ActiveComponent ? (
            // Passer les props nécessaires selon le widget
            (["security", "config", "emails", "audrey", "controle-conformite"].includes(activeWidget)) ? (
              <ActiveComponent
                currentUser={effectiveUser}
                currentTenant={currentTenant}
                tenants={tenants}
                onRefreshStats={loadStats}
              />
            ) : (
              <ActiveComponent onRefreshStats={loadStats} />
            )
          ) : (
            <div className="flex items-center justify-center h-64">
              <p style={{ color: "var(--oh-text-muted)" }} data-testid="gouv-acces-refuse">
                {groupesVisibles.length === 0 ? "Cet espace est réservé aux administrateurs." : "Widget non disponible"}
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
